import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Tooltip } from "@heroui/react";
import { LuChevronDown } from "react-icons/lu";
import clsx from "clsx";
import { motion, AnimatePresence } from "framer-motion";
import { useUser } from "@/context/UserContext";

const MenuItem = ({ item, depth = 0, isCollapsed, onNavigate }) => {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const { hasPermission } = useUser();

    // Filter visible children (exclude hidden type and check permission)
    const visibleChildren =
        item.children?.filter(
            (child) =>
                child.type !== "hidden" && hasPermission(child.permission),
        ) || [];
    const hasVisibleChildren = visibleChildren.length > 1;
    const hasSingleChild = visibleChildren.length === 1;

    // If only 1 visible child, use that child's path and render as direct link
    const effectivePath = hasSingleChild ? visibleChildren[0].path : item.path;
    const effectiveTitle = item.title;

    const isActive = location.pathname === effectivePath;
    const isChildActive =
        hasVisibleChildren &&
        visibleChildren.some((child) => location.pathname === child.path);

    // Auto-expand if child is active AND NOT collapsed
    if (isChildActive && !isOpen && !isCollapsed) {
        setIsOpen(true);
    }

    const toggleOpen = (e) => {
        if (hasVisibleChildren && !isCollapsed) {
            e.preventDefault();
            setIsOpen(!isOpen);
        } else if (!hasVisibleChildren) {
            onNavigate?.();
        }
    };

    return (
        <div className="relative">
            {/* Main Item Link */}
            <Tooltip
                content={effectiveTitle}
                placement="right"
                isDisabled={!isCollapsed}
                closeDelay={0}
            >
                <div>
                    <NavLink
                        to={effectivePath || "#"}
                        onClick={toggleOpen}
                        className={({ isActive }) =>
                            clsx(
                                "group flex items-center rounded-lg px-3 py-1.5 text-sm font-medium transition-colors duration-200",
                                isCollapsed
                                    ? "justify-center px-1"
                                    : "justify-between",
                                depth > 0 ? "py-1.5" : "py-2", // Reduced padding for submenu
                                // Active State for Items WITHOUT children (Leaf nodes)
                                !hasVisibleChildren && isActive
                                    ? "bg-primary-50 text-primary-600 dark:bg-primary-900/10 dark:text-primary-400"
                                    : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100",
                                // Active State for Items WITH children (Parent nodes)
                                hasVisibleChildren &&
                                    (isActive || isChildActive) &&
                                    "text-primary-600 dark:text-primary-400",
                                "border border-transparent", // Layout stability
                            )
                        }
                    >
                        <div
                            className={clsx(
                                "flex items-center gap-3",
                                isCollapsed && "w-full justify-center",
                            )}
                        >
                            {item.icon && (
                                <span
                                    className={clsx(
                                        "text-lg transition-colors",
                                        !hasVisibleChildren && isActive
                                            ? "text-primary-600 dark:text-primary-400"
                                            : hasVisibleChildren &&
                                                (isActive || isChildActive)
                                              ? "text-primary-600 dark:text-primary-400"
                                              : "text-zinc-500 group-hover:text-zinc-700 dark:text-zinc-400 dark:group-hover:text-zinc-300",
                                    )}
                                >
                                    {item.icon}
                                </span>
                            )}
                            {!isCollapsed && <span>{effectiveTitle}</span>}
                        </div>
                        {hasVisibleChildren && !isCollapsed && (
                            <span
                                className={clsx(
                                    "transition-transform duration-200",
                                    isOpen && "rotate-180",
                                    isActive || isChildActive
                                        ? "text-primary-500"
                                        : "text-zinc-400",
                                )}
                            >
                                <LuChevronDown size={14} />
                            </span>
                        )}
                    </NavLink>
                </div>
            </Tooltip>

            {/* Submenu Children with Tree Line - HIDE if collapsed */}
            <AnimatePresence>
                {hasVisibleChildren && isOpen && !isCollapsed && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="relative mt-1 ml-5 border-l border-zinc-200 pl-3 dark:border-zinc-800">
                            {/* The tree line is created by the border-l on this wrapper div */}
                            {visibleChildren.map((child, index) => (
                                <MenuItem
                                    key={index}
                                    item={child}
                                    depth={depth + 1}
                                    isCollapsed={isCollapsed}
                                    onNavigate={onNavigate}
                                />
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const SidebarSection = ({ section, isCollapsed, onNavigate }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const { hasPermission } = useUser();

    const visibleItems = section.items.filter((item) => {
        if (item.type === "hidden") return false;
        if (item.permission) return hasPermission(item.permission);
        if (item.children) {
            return item.children.some(
                (c) => c.type !== "hidden" && hasPermission(c.permission),
            );
        }
        return true;
    });

    if (visibleItems.length === 0) return null;

    return (
        <div className="mb-6">
            {section.title && !isCollapsed && (
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="group mb-2 flex w-full items-center justify-between px-3"
                >
                    <h3 className="text-xs font-bold tracking-wider text-zinc-500 uppercase transition-colors group-hover:text-zinc-700 dark:text-zinc-400 dark:group-hover:text-zinc-300">
                        {section.title}
                    </h3>
                    <span
                        className={clsx(
                            "text-zinc-400 transition-transform duration-200",
                            !isExpanded && "-rotate-90",
                        )}
                    >
                        <LuChevronDown size={12} />
                    </span>
                </button>
            )}

            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-0.5 overflow-hidden"
                    >
                        {visibleItems.map((item, itemIndex) => (
                            <MenuItem
                                key={itemIndex}
                                item={item}
                                isCollapsed={isCollapsed}
                                onNavigate={onNavigate}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const SidebarMenu = ({ items, isCollapsed, onNavigate }) => {
    return (
        <nav
            className={clsx(
                "hide-scrollbar flex-1 overflow-y-auto py-4",
                isCollapsed ? "px-2" : "px-3",
            )}
        >
            {items.map((section, index) => (
                <SidebarSection
                    key={index}
                    section={section}
                    isCollapsed={isCollapsed}
                    onNavigate={onNavigate}
                />
            ))}
        </nav>
    );
};

export default SidebarMenu;
